/**
 * Parses a Beschreibung (Gewerke) CSV file into ViewerSection[] for the DataViewer.
 *
 * The CSV uses semicolons as delimiters and groups rows by a numeric section prefix.
 * Section 0 is the project header (no column header row).
 * All other sections have a header row (first occurrence of that section number)
 * followed by data rows sharing the same prefix.
 *
 * Known section numbers and their names:
 *  0 = Projektinfo, 1 = Wohnungen, 2 = Räume, 3 = Gewerke (CWI),
 *  4 = Material, 5 = Zeitschätzung, 6 = Zeiterfassung,
 *  7 = Bauteilaufbau, 9 = Gewerke Änderungen, 10 = Arbeitsschritte
 */
import { ViewerSection, ViewerColumn } from "@/components/common/data-viewer/DataViewerModels";
import { i18n } from "@/plugins/vuetify";

/** Human-readable section names keyed by CSV section prefix */
const SECTION_NAMES: Record<string, string> = {
  "0": "beschreibungViewer.sections.projectInfo",
  "1": "beschreibungViewer.sections.apartments",
  "2": "beschreibungViewer.sections.rooms",
  "3": "beschreibungViewer.sections.workItems",
  "4": "beschreibungViewer.sections.materials",
  "5": "beschreibungViewer.sections.timeEstimate",
  "6": "beschreibungViewer.sections.timeTracking",
  "7": "beschreibungViewer.sections.buildingStructure",
  "9": "beschreibungViewer.sections.workItemChanges",
  "10": "beschreibungViewer.sections.tasks",
};

/** Hardcoded column labels for section 0 (project header — has no header row) */
const SECTION_0_COLUMNS = ["projectId", "address", "dateTime", "lastName", "firstName"];

/** Sections that should be expanded by default */
const EXPANDED_SECTIONS = new Set(["0", "1", "3"]);

/** Builds section 0 (project header) — single row, no header in CSV */
function buildSection0(rows: string[][]): ViewerSection | null {
  if (rows.length === 0) return null;

  const columns: ViewerColumn[] = SECTION_0_COLUMNS.map((key, index) => ({
    key: `col_${index}`,
    label: i18n.t(`beschreibungViewer.fields.${key}`) as string,
    searchable: true,
    visible: true,
  }));

  const mappedRows = rows.map((fields) => {
    const row: Record<string, string | number | null> = {};
    columns.forEach((col, index) => {
      const value = fields[index]?.trim() ?? null;
      row[col.key] = value === "" ? null : value;
    });
    return row;
  });

  const nameKey = SECTION_NAMES["0"];

  return {
    id: "section_0",
    name: i18n.t(nameKey) as string,
    type: "0",
    columns,
    rows: mappedRows,
    expandedByDefault: true,
  };
}

/**
 * Builds a ViewerSection from grouped CSV rows.
 * Section 0 is special (no header row). Others use the first row as headers.
 */
function buildSection(sectionId: string, rows: string[][]): ViewerSection | null {
  if (sectionId === "0") {
    return buildSection0(rows);
  }

  // First row is the header
  if (rows.length < 2) return null; // Only header, no data

  const headerFields = rows[0];
  const dataRows = rows.slice(1);

  const columns: ViewerColumn[] = headerFields.map((label, index) => ({
    key: `col_${index}`,
    label: label.trim(),
    searchable: true,
    visible: true,
  }));

  const mappedRows = dataRows.map((fields) => {
    const row: Record<string, string | number | null> = {};
    columns.forEach((col, index) => {
      const value = fields[index]?.trim() ?? null;
      row[col.key] = value === "" ? null : value;
    });
    return row;
  });

  const nameKey = SECTION_NAMES[sectionId];
  const name = nameKey ? (i18n.t(nameKey) as string) : `Section ${sectionId}`;

  return {
    id: `section_${sectionId}`,
    name,
    type: sectionId,
    columns,
    rows: mappedRows,
    expandedByDefault: EXPANDED_SECTIONS.has(sectionId),
  };
}

/**
 * Parses raw CSV text into ViewerSection[] for the DataViewer component.
 * @param csvText Raw semicolon-delimited CSV content
 * @returns Sections in the order they first appear in the file
 */
export function parseBeschreibungCsv(csvText: string): ViewerSection[] {
  const lines = csvText.split(/\r?\n/).filter((line) => line.trim().length > 0);
  if (lines.length === 0) return [];

  // Group lines by section number (first field before the first semicolon)
  const sectionGroups = new Map<string, string[][]>();
  const sectionOrder: string[] = [];

  for (const line of lines) {
    const fields = line.split(";");
    const sectionId = fields[0];

    // Skip lines that don't start with a number
    if (!/^\d+$/.test(sectionId)) continue;

    if (!sectionGroups.has(sectionId)) {
      sectionGroups.set(sectionId, []);
      sectionOrder.push(sectionId);
    }
    // Store fields without the section prefix
    const group = sectionGroups.get(sectionId);
    if (group) group.push(fields.slice(1));
  }

  const sections: ViewerSection[] = [];

  for (const sectionId of sectionOrder) {
    const rows = sectionGroups.get(sectionId) ?? [];
    if (rows.length === 0) continue;

    const section = buildSection(sectionId, rows);
    if (section) sections.push(section);
  }

  return sections;
}
